import { BLOCKS, helpers, INLINES } from '@contentful/rich-text-types';
import { createContext, createMemo, For, Match, Show, Switch, useContext } from 'solid-js';
import {
	Button,
	HorizontalRule,
	Picture,
	Section,
	Link as UILink,
	TextLink,
	Heading as UIHeading,
	Card,
	MediaSection,
} from '@troon/ui';
import { Dynamic } from 'solid-js/web';
import type {
	Block,
	Document as RichTextDocument,
	Inline,
	Text as TextNode,
	Node,
	TopLevelBlock,
} from '@contentful/rich-text-types';
import type { Component, ComponentProps, ParentProps, ValidComponent } from 'solid-js';
import type {
	TypeButtonSkeleton,
	TypeCardSkeleton,
	TypePageSkeleton,
	TypePersonSkeleton,
	TypeSectionSkeleton,
} from './__generated__';
import type { Entry } from 'contentful';

function NoOp(props: ParentProps) {
	return <>{props.children}</>;
}

const RichTextContext = createContext<{ headingOffset: number; itemWrapper: Record<string, ValidComponent> }>({
	headingOffset: 0,
	itemWrapper: {},
});

type Props = {
	document: RichTextDocument;
	/**
	 * Provide a heading offset to change the size of headings per their level.
	 * @example
	 * Change `as="h2"` to appear as `size="h4"`
	 * ```ts
	 * headingOffset={2}
	 * ```
	 */
	headingOffset?: number;
	entryGroupWrapper?: Record<string, Component<ParentProps>>;
	itemWrapper?: Record<string, Component<ParentProps>>;
};

const reducer =
	(wrapper: Record<string, Component<ParentProps>> = {}) =>
	(acc: Array<TopLevelBlock | WrappedList>, item: TopLevelBlock) => {
		let prev = acc[acc.length - 1];
		const itemType: keyof typeof wrapper = item.data.target?.sys.contentType?.sys.id;
		if (item.nodeType === BLOCKS.EMBEDDED_ENTRY && wrapper && wrapper[itemType]) {
			if (!prev || !('wrapper' in prev)) {
				acc.push({ wrapper: wrapper[itemType]! ?? NoOp, children: [] });
				prev = acc[acc.length - 1];
			}

			if (prev && 'wrapper' in prev) {
				prev.children.push(item);
				return acc;
			}
		}

		if (item.nodeType === BLOCKS.PARAGRAPH) {
			if (item.content[0]?.nodeType === 'text' && (item.content[0] as TextNode).value?.trim() === '') {
				item.content.shift();
			}
			if (
				item.content[item.content.length - 1]?.nodeType === 'text' &&
				(item.content[item.content.length - 1] as TextNode).value?.trim() === ''
			) {
				item.content.pop();
			}

			if (item.content.length === 0) {
				return acc;
			}
		}

		acc.push(item);

		return acc;
	};

type WrappedList = { wrapper: Component<ParentProps>; children: Array<Block> };

export function RichText(props: Props) {
	const content = createMemo(() =>
		props.document.content.reduce(reducer(props.entryGroupWrapper), [] as Array<TopLevelBlock | WrappedList>),
	);

	return (
		<RichTextContext.Provider
			// eslint-disable-next-line solid/reactivity
			value={{ headingOffset: props.headingOffset ?? 0, itemWrapper: props.itemWrapper ?? {} }}
		>
			<Section class="[&_ol]:list-decimal [&_ol_ol]:list-[upper-alpha] [&_ol_ol_ol]:list-[lower-roman] [&_ul]:list-disc [&_ul_ul]:list-[circle] [&_ul_ul_ul]:list-[square]">
				<For each={content()}>
					{(item) => (
						<Switch>
							<Match when={'wrapper' in item && (item as WrappedList)}>
								{(item) => (
									<Dynamic component={item().wrapper}>
										<For each={item().children}>
											{(child) => <Dynamic component={components[child.nodeType]} {...child} />}
										</For>
									</Dynamic>
								)}
							</Match>
							<Match when>
								<Dynamic component={components[(item as TopLevelBlock).nodeType]} {...item} />
							</Match>
						</Switch>
					)}
				</For>
			</Section>
		</RichTextContext.Provider>
	);
}

export function richTextToPlain(rootNode: Block | Inline, blockDivisor: string = ' '): string {
	if (!rootNode || !rootNode.content || !Array.isArray(rootNode.content)) {
		return '';
	}

	return (rootNode as Block).content.reduce((acc: string, node: Node, i: number): string => {
		let nodeTextValue: string = '';

		if (helpers.isText(node)) {
			nodeTextValue = node.value;
		} else if (helpers.isBlock(node) || helpers.isInline(node)) {
			nodeTextValue = richTextToPlain(node, blockDivisor);
			if (!nodeTextValue.length) {
				return acc;
			}
		}

		const nextNode = rootNode.content[i + 1];
		const isNextNodeBlock = nextNode && helpers.isBlock(nextNode);
		const divisor = isNextNodeBlock ? blockDivisor : '';
		return `${acc}${nodeTextValue}${divisor}`;
	}, '');
}

function Paragraph(props: Block) {
	return (
		<Show
			// Filter out empty paragraphs
			when={
				Array.isArray(props.content) &&
				!(props.content.length === 1 && props.content[0]?.nodeType === 'text' && props.content[0]?.value?.trim() === '')
			}
		>
			<p>
				<For each={props.content}>{(item) => <Dynamic component={components[item.nodeType]} {...item} />}</For>
			</p>
		</Show>
	);
}

function Heading(props: Block) {
	const ctx = useContext(RichTextContext);
	const level = createMemo(() => {
		const level = nodeTypeToHeadingLevel[props.nodeType as keyof typeof nodeTypeToHeadingLevel];
		const levels = Object.values(nodeTypeToHeadingLevel);
		const levelIndex = levels.indexOf(level)!;
		return { level, size: levels[levelIndex + ctx.headingOffset] };
	});
	return (
		<UIHeading as={level().level} size={level().size}>
			<For each={props.content}>{(item) => <Dynamic component={components[item.nodeType]} {...item} />}</For>
		</UIHeading>
	);
}
const nodeTypeToHeadingLevel = {
	'heading-1': 'h1',
	'heading-2': 'h2',
	'heading-3': 'h3',
	'heading-4': 'h4',
	'heading-5': 'h5',
	'heading-6': 'h6',
} as const;

function Text(props: TextNode) {
	return (
		<Show
			when={props.marks[0]}
			fallback={
				<For each={props.value.split('\n')}>
					{(item, i) => (
						<>
							{item}
							<Show when={i() < props.value.split('\n').length - 1}>
								<br />
							</Show>
						</>
					)}
				</For>
			}
		>
			{(mark) => (
				<Dynamic component={mark().type === 'bold' ? 'b' : mark().type === 'italic' ? 'i' : 'span'}>
					{props.value}
				</Dynamic>
			)}
		</Show>
	);
}

function Link(props: Inline) {
	return (
		<TextLink href={props.data.uri}>
			<For each={props.content}>{(item) => <Dynamic component={components[item.nodeType]} {...item} />}</For>
		</TextLink>
	);
}

function UnorderedList(props: Block) {
	return (
		<ul class="flex flex-col gap-2 ps-6">
			<For each={props.content}>{(item) => <Dynamic component={components[item.nodeType]} {...item} />}</For>
		</ul>
	);
}
function OrderedList(props: Block) {
	return (
		<ol class="flex flex-col gap-2 ps-6">
			<For each={props.content}>{(item) => <Dynamic component={components[item.nodeType]} {...item} />}</For>
		</ol>
	);
}
function ListItem(props: Block) {
	return (
		<li>
			<For each={props.content}>{(item) => <Dynamic component={components[item.nodeType]} {...item} />}</For>
		</li>
	);
}

function Asset(props: Block) {
	return (
		<Switch>
			<Match when={props.data.target.fields.file.contentType.startsWith('image/')}>
				<Picture
					src={props.data.target.fields.file.url}
					width={800}
					height={Math.round(
						800 /
							(props.data.target.fields.file.details.image.width / props.data.target.fields.file.details.image.height),
					)}
					sizes="(min-width: 1440px) 50vw, 95vw"
					crop="center"
					mode="contain"
					alt={props.data.target.fields.description ?? props.data.target.fields.title}
					class="w-full rounded-lg"
				/>
			</Match>
			<Match when>
				<Button as={UILink} href={props.data.target.fields.file.url} class="size-fit grow-0">
					{props.data.target.fields.title}
				</Button>
			</Match>
		</Switch>
	);
}

function Table(props: Block) {
	return (
		<table>
			<For each={props.content}>{(item) => <Dynamic component={components[item.nodeType]} {...item} />}</For>
		</table>
	);
}

function Tr(props: Block) {
	return (
		<tr>
			<For each={props.content}>{(item) => <Dynamic component={components[item.nodeType]} {...item} />}</For>
		</tr>
	);
}

function Th(props: Block) {
	return (
		<th class="border border-white bg-brand-700 px-4 py-2 text-start text-white">
			<For each={props.content}>{(item) => <Dynamic component={components[item.nodeType]} {...item} />}</For>
		</th>
	);
}

function Td(props: Block) {
	return (
		<td class="border border-neutral px-4 py-2">
			<For each={props.content}>{(item) => <Dynamic component={components[item.nodeType]} {...item} />}</For>
		</td>
	);
}

function EmbeddedEntry(props: Block) {
	const ctx = useContext(RichTextContext);

	return (
		<Dynamic component={ctx.itemWrapper[props.data.target?.sys.contentType?.sys.id] ?? 'div'}>
			<Switch>
				<Match
					when={
						props.data.target?.sys.contentType?.sys.id === 'button' &&
						(props.data.target.fields as Entry<TypeButtonSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>['fields'])
					}
				>
					{(field) => (
						<Button as={UILink} href={field().url} appearance={field().appearance}>
							{field().content}
						</Button>
					)}
				</Match>
				<Match
					when={
						props.data.target?.sys.contentType?.sys.id === 'section' &&
						(props.data.target.fields as Entry<TypeSectionSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>['fields'])
					}
				>
					{(fields) => {
						let props: Partial<ComponentProps<typeof MediaSection>> = {};
						if (fields().youtubeId) {
							props = {
								type: 'video',
								videoProvider: 'youtube',
								code: fields().youtubeId as string,
							};
						}
						return (
							<MediaSection
								reverse={!fields().direction}
								// @ts-expect-error for some reason typescript thinks this could be something other than string?
								src={fields().media?.fields.file?.url || ''}
								appearance={fields().appearance}
								{...props}
							>
								<RichText document={fields().content} />
							</MediaSection>
						);
					}}
				</Match>
				<Match
					when={
						props.data.target?.sys.contentType?.sys.id === 'page' &&
						(props.data.target.fields as Entry<TypePageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>['fields'])
					}
				>
					{(fields) => (
						<Card
							img={fields().logo?.fields.file?.url}
							title={fields().title}
							url={fields().slug}
							linkText={
								<>
									View more<span class="sr-only"> about {fields().title}</span>
								</>
							}
						>
							<p>{fields().summary}</p>
						</Card>
					)}
				</Match>
				<Match
					when={
						props.data.target?.sys.contentType?.sys.id === 'card' &&
						(props.data.target.fields as Entry<TypeCardSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>['fields'])
					}
				>
					{(fields) => (
						<Card
							img={fields().logo?.fields.file?.url}
							title={fields().title}
							banner={fields().banner?.fields.file?.url}
							url={fields().url as string}
							linkText={fields().linkText as string}
						>
							<RichText document={fields().content} />
						</Card>
					)}
				</Match>
				<Match
					when={
						props.data.target?.sys.contentType?.sys.id === 'person' &&
						(props.data.target.fields as Entry<TypePersonSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>['fields'])
					}
				>
					{(fields) => (
						<div class="flex items-stretch gap-4 md:gap-8">
							<Picture
								src={fields().photo?.fields.file?.url}
								width={300}
								height={400}
								sizes="(min-width: 1024px) 25vw, 50vw"
								class="shrink-0 basis-1/4 rounded-lg"
							/>
							<div class="flex flex-col gap-4">
								<UIHeading as="h3" size="h4">
									{fields().name}
								</UIHeading>
								<Show when={fields().bio}>{(bio) => <RichText document={bio()} />}</Show>
							</div>
						</div>
					)}
				</Match>
			</Switch>
		</Dynamic>
	);
}

function InlineEntry(props: Block) {
	return (
		<Switch>
			<Match
				when={
					props.data.target?.sys.contentType?.sys.id === 'button' &&
					(props.data.target.fields as Entry<TypeButtonSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>['fields'])
				}
			>
				{(field) => (
					<Button
						as={UILink}
						href={field().url}
						class="me-4 inline-flex size-auto last:me-0"
						appearance={field().appearance}
					>
						{field().content}
					</Button>
				)}
			</Match>
		</Switch>
	);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const components: Record<BLOCKS | INLINES | 'text', Component<any>> = {
	[BLOCKS.DOCUMENT]: () => null,
	[BLOCKS.PARAGRAPH]: Paragraph,
	[BLOCKS.HEADING_1]: Heading,
	[BLOCKS.HEADING_2]: Heading,
	[BLOCKS.HEADING_3]: Heading,
	[BLOCKS.HEADING_4]: Heading,
	[BLOCKS.HEADING_5]: Heading,
	[BLOCKS.HEADING_6]: Heading,
	[BLOCKS.UL_LIST]: UnorderedList,
	[BLOCKS.OL_LIST]: OrderedList,
	[BLOCKS.LIST_ITEM]: ListItem,
	[BLOCKS.HR]: HorizontalRule,
	[BLOCKS.QUOTE]: Paragraph,
	[BLOCKS.EMBEDDED_ASSET]: Asset,
	[BLOCKS.EMBEDDED_ENTRY]: EmbeddedEntry,
	[BLOCKS.EMBEDDED_RESOURCE]: () => null,
	[BLOCKS.TABLE]: Table,
	[BLOCKS.TABLE_ROW]: Tr,
	[BLOCKS.TABLE_HEADER_CELL]: Th,
	[BLOCKS.TABLE_CELL]: Td,
	text: Text,
	[INLINES.HYPERLINK]: Link,
	[INLINES.ASSET_HYPERLINK]: Link,
	[INLINES.EMBEDDED_RESOURCE]: () => null,
	[INLINES.EMBEDDED_ENTRY]: InlineEntry,
	[INLINES.ENTRY_HYPERLINK]: () => null,
	[INLINES.RESOURCE_HYPERLINK]: () => null,
};
